import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

interface League {
  id: string;
  name: string;
  puzzlesPerDay: number;
  inviteCode?: string;
  memberCount: number;
  role: string;
}

export function LeaguesPage() {
  const { user, loading: authLoading } = useAuth();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [name, setName] = useState('');
  const [puzzlesPerDay, setPuzzlesPerDay] = useState(3);
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    api<{ leagues: League[] }>('/leagues').then((d) => setLeagues(d.leagues)).catch(() => {});
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    if (!user.isPremium) {
      navigate('/premium', { replace: true });
      return;
    }
    load();
  }, [authLoading, user, navigate]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/leagues', {
        method: 'POST',
        body: JSON.stringify({ name, puzzlesPerDay }),
      });
      setName('');
      setShowCreate(false);
      load();
    } catch (err: unknown) {
      const code = (err as { data?: { error?: string } }).data?.error;
      setError(code || t('leagues.createError'));
    } finally {
      setBusy(false);
    }
  };

  const handleJoin = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/leagues/join', {
        method: 'POST',
        body: JSON.stringify({ inviteCode: inviteCode.trim() }),
      });
      setInviteCode('');
      load();
    } catch (err: unknown) {
      const code = (err as { data?: { error?: string } }).data?.error;
      setError(code || t('leagues.joinError'));
    } finally {
      setBusy(false);
    }
  };

  if (authLoading) {
    return (
      <main className="page leagues">
        <p className="loading">{t('common.loading')}</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="page leagues">
        <div className="profile-card profile-card--empty">
          <p>{t('leagues.loginPrompt')}</p>
          <Link to="/login" className="btn primary block">{t('landing.login')}</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page leagues">
      <div className="profile-card">
        <h1>{t('leagues.title')}</h1>

        <div className="leagues-actions">
          <button type="button" className="btn primary" onClick={() => setShowCreate(!showCreate)}>
            {t('leagues.create')}
          </button>
        </div>

        {showCreate && (
          <form className="league-form" onSubmit={handleCreate}>
            <label>
              {t('leagues.name')}
              <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
            </label>
            <label>
              {t('leagues.puzzlesPerDay')}
              <input
                type="number"
                min={1}
                max={10}
                value={puzzlesPerDay}
                onChange={(e) => setPuzzlesPerDay(Number(e.target.value))}
              />
            </label>
            <button type="submit" className="btn secondary block" disabled={busy}>
              {t('leagues.createSubmit')}
            </button>
          </form>
        )}

        <form className="league-form" onSubmit={handleJoin}>
          <label>
            {t('leagues.inviteCode')}
            <input
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="abc123"
            />
          </label>
          <button type="submit" className="btn secondary block" disabled={busy}>
            {t('leagues.join')}
          </button>
        </form>

        {error && <p className="error" role="alert">{error}</p>}

        <ul className="leagues-list">
          {leagues.map((l) => (
            <li key={l.id}>
              <Link to={`/leagues/${l.id}`} className="league-card-link">
                <strong>{l.name}</strong>
                <span className="muted">
                  {l.memberCount} {t('leagues.members')} · {l.puzzlesPerDay}/j
                </span>
              </Link>
            </li>
          ))}
        </ul>
        {leagues.length === 0 && <p className="muted">{t('leagues.empty')}</p>}
      </div>
    </main>
  );
}
